import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/UserContextProvider";
import i18n from "../../../language/i18n";
import {
  createAnnouncementActionByAnnouncementId,
  createAnnouncementActionsByAnnouncementIdList,
} from "../../../requests/NotificationRequests";
import {
  BaseLoading,
  Calendar,
  EmptyState,
  IconButton,
  PageTravelGroup as _PageTravelGroup,
  SecondaryButton,
  SelectionTab as _SelectionTab,
} from "../../../library";
import { showFileByFileTypeAndUrl } from "../../../utilization/StorageUtilization";
import { buildLearnerCalendarEvents } from "../../../utilization/CalendarUtilization";
import { announcementListViewStyles } from "./AnnouncementListViewStyles";

// TODO(ts-tighten): library types are over-strict; cast lets call sites match real runtime contract.
const SelectionTab: any = _SelectionTab;
const PageTravelGroup: any = _PageTravelGroup;

// Announcements paginate (page-travel) instead of scrolling — a fixed page of
// rows keeps the card height predictable and avoids a long inner scrollbar.
const PAGE_SIZE = 6;

interface AnnouncementListViewProps {
  isMinimized?: boolean;
}

// Scope (announcement.type) → tint/ink coding, the app analog of the design's
// per-subject color system (announcements have no subject, but they DO have a
// scope — personal / branch / class / school-wide).
const SCOPE_STYLE: Record<string, { tint: string; ink: string }> = {
  USER: { tint: "var(--accent-coral-bg)", ink: "var(--accent-coral-text, #D9613F)" },
  USER_PROFILE: { tint: "var(--accent-coral-bg)", ink: "var(--accent-coral-text, #D9613F)" },
  BRANCH: { tint: "var(--brand-50)", ink: "var(--brand-dark)" },
  GRADE: { tint: "var(--accent-gold-bg)", ink: "var(--accent-gold-text)" },
  SEASON: { tint: "var(--accent-purple-bg)", ink: "var(--accent-purple-text)" },
  INSTITUTION: { tint: "var(--accent-purple-bg)", ink: "var(--accent-purple-text)" },
  CAMPUS: { tint: "var(--accent-purple-bg)", ink: "var(--accent-purple-text)" },
  BRAND: { tint: "var(--accent-purple-bg)", ink: "var(--accent-purple-text)" },
};
const scopeStyle = (type: string) =>
  SCOPE_STYLE[type] || { tint: "var(--brand-50)", ink: "var(--brand-dark)" };

// Inline "announce" glyph (currentColor) — a faithful port of the design's AnnIcon.
const BellGlyph = ({ size = 20 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}
    aria-hidden="true"
  >
    <path d="M3 11v2a1 1 0 0 0 1 1h2l4.5 3.5a1 1 0 0 0 1.6-.8V7.3a1 1 0 0 0-1.6-.8L6 10H4a1 1 0 0 0-1 1Z" />
    <path d="M16 9a3.5 3.5 0 0 1 0 6" />
    <path d="M18.5 6.5a7 7 0 0 1 0 11" />
  </svg>
);

const AnnouncementListView = (props: AnnouncementListViewProps) => {
  const { isMinimized } = props;
  const navigate = useNavigate();
  const {
    language,
    userInformation,
    userProfile,
    announcementDTOList,
    announcementListLoading,
    numberOfUnreadAnnouncements,
    setNumberOfUnreadAnnouncements,
    setAnnouncementDTOList,
    activeContentList,
  } = useAuth();
  const [tab, setTab] = useState("all");
  const [pageNumber, setPageNumber] = useState(0);
  const [openId, setOpenId] = useState<any>(null);

  const retrieveCreatedForText = (type: string) => {
    if (type === "USER") return i18n.t("announcement_created_for_user");
    if (type === "USER_PROFILE")
      return i18n.t("announcement_created_for_user_profile");
    if (type === "BRAND") return i18n.t("announcement_created_for_brand");
    if (type === "CAMPUS") return i18n.t("announcement_created_for_campus");
    if (type === "INSTITUTION")
      return i18n.t("announcement_created_for_institution");
    if (type === "SEASON") return i18n.t("announcement_created_for_season");
    if (type === "GRADE") return i18n.t("announcement_created_for_grade");
    if (type === "BRANCH") return i18n.t("announcement_created_for_branch");
    return "";
  };

  const isUnread = (a: any) => a.actionType !== "READ";

  const formatDate = (createdAt: any) =>
    new Date(createdAt).toLocaleString(language, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const markRead = async (announcement: any) => {
    const { err } = await createAnnouncementActionByAnnouncementId({
      userId: userInformation.id,
      userProfileId: userProfile.id,
      announcement: { id: announcement.id, type: announcement.type },
      type: "READ",
    });
    if (err) {
      return;
    }
    setAnnouncementDTOList(
      announcementDTOList.map((dto: any) =>
        dto.id === announcement.id ? { ...dto, actionType: "READ" } : dto,
      ),
    );
    setNumberOfUnreadAnnouncements(Math.max(0, numberOfUnreadAnnouncements - 1));
  };

  const handleOpen = (announcement: any) => {
    // Toggle inline expansion (accordion) instead of swapping to a detail page.
    const willOpen = openId !== announcement.id;
    setOpenId(willOpen ? announcement.id : null);
    if (willOpen && isUnread(announcement)) markRead(announcement);
  };

  const handleMarkAllRead = async () => {
    const unread = announcementDTOList.filter(isUnread);
    if (!unread.length) return;
    // One bulk request instead of N parallel single-announcement actions.
    await createAnnouncementActionsByAnnouncementIdList(
      unread.map((a: any) => ({
        userId: userInformation.id,
        userProfileId: userProfile.id,
        announcement: { id: a.id, type: a.type },
        type: "READ",
      })),
    );
    setAnnouncementDTOList(
      announcementDTOList.map((dto: any) => ({ ...dto, actionType: "READ" })),
    );
    setNumberOfUnreadAnnouncements(0);
  };

  const unreadCount = announcementDTOList.filter(isUnread).length;
  const filtered =
    tab === "unread" ? announcementDTOList.filter(isUnread) : announcementDTOList;
  const totalPageNumber = Math.ceil(filtered.length / PAGE_SIZE);
  // Clamp so a shrinking list (e.g. switching to a near-empty tab) never lands
  // on an out-of-range, empty page.
  const safePage = Math.min(pageNumber, Math.max(0, totalPageNumber - 1));
  const paginated = filtered.slice(
    safePage * PAGE_SIZE,
    (safePage + 1) * PAGE_SIZE,
  );

  // Unified calendar feed (şube/destek ödevi + exam start+due + announcement "posted"
  // markers) rendered by the shared <Calendar/> primitive — same builder the dashboard
  // calendar uses, so the two read identically. Group rows ride inside activeContentList
  // since the Çalışmalar fold, so there is no second list to pass.
  const calendarEvents = useMemo(
    () =>
      buildLearnerCalendarEvents({
        activeContentList,
        announcementDTOList,
      }),
    [activeContentList, announcementDTOList, language],
  );

  // ── Glass row (click to expand inline) ──────────────────────
  const RenderRow = (a: any, onClick?: () => void) => {
    const s = scopeStyle(a.type);
    const unread = isUnread(a);
    // `onClick` override (dashboard widget) navigates to the full list — no
    // inline detail there. On the full list, clicking toggles the row open.
    const open = !onClick && openId === a.id;
    return (
      <div
        key={a.id}
        className={`qbw-row${unread ? " is-unread" : ""}${open ? " is-open" : ""}`}
      >
        <button
          type="button"
          className="qbw-row-summary"
          aria-expanded={open}
          onClick={onClick || (() => handleOpen(a))}
        >
          <span
            className="qbw-row-tile"
            style={{ background: s.tint, color: s.ink }}
          >
            <BellGlyph size={22} />
          </span>
          <span className="qbw-row-body">
            {unread ? (
              <span className="qbw-row-dot" aria-label="unread" />
            ) : null}
            <span className="qbw-row-title">{i18n.t(a.name)}</span>
            {!open ? (
              <span className="qbw-row-text">{i18n.t(a.description)}</span>
            ) : null}
            <span className="qbw-row-meta">
              <span className="qbw-row-date">{formatDate(a.createdAt)}</span>
              <span className="qbw-row-sep">·</span>
              <span className="qbw-row-scope" style={{ color: s.ink }}>
                {retrieveCreatedForText(a.type)}
              </span>
            </span>
          </span>
          <span className="qbw-row-chev" aria-hidden="true">
            ›
          </span>
        </button>
        {open ? (
          <div className="qbw-row-detail">
            <div className="qbw-row-detail-divider" />
            <p className="qbw-row-fulltext">{i18n.t(a.description)}</p>
            {a.fileType && a.fileUrl ? (
              <div className="qbw-row-file">
                <div className="qbw-file-preview">
                  {showFileByFileTypeAndUrl(a.fileType, a.fileUrl)}
                </div>
                <IconButton
                  name="download"
                  size="small"
                  onClick={() => window.open(a.fileUrl, "_blank")}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const RenderFullContent = () => {
    if (announcementListLoading) {
      return (
        <div className="qbw-loading">
          <BaseLoading
            size="large"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        </div>
      );
    }
    return (
      <div className="qbw-grid-top">
        {/* Announcements card (no title — the page chrome already labels it) */}
        <div className="qbw-anncard" data-tour="ann-list">
          <div className="qbw-anncard-head">
            <SelectionTab
              optionList={[
                {
                  id: "all",
                  title: i18n.t("filter_all"),
                  count: announcementDTOList.length,
                },
                { id: "unread", title: i18n.t("unread"), count: unreadCount },
              ]}
              selectedId={tab}
              handleSelectOnClick={(id: string) => {
                setTab(id);
                setPageNumber(0);
              }}
            />
            {unreadCount > 0 ? (
              <SecondaryButton
                {...({
                  title: i18n.t("mark_all_read"),
                  size: "small",
                  textOnly: true,
                  onClick: handleMarkAllRead,
                } as any)}
              />
            ) : null}
          </div>
          {filtered.length === 0 ? (
            <EmptyState
              icon={tab === "unread" ? "confetti" : "tray"}
              title={
                tab === "unread"
                  ? i18n.t("all_caught_up")
                  : i18n.t("no_announcements")
              }
              description={
                tab === "unread"
                  ? i18n.t("all_caught_up_text")
                  : i18n.t("no_announcements_text")
              }
            />
          ) : (
            <>
              <div className="qbw-rows qbw-rows--panel">
                {paginated.map((a: any) => RenderRow(a))}
              </div>
              {totalPageNumber > 1 ? (
                <div className="qbw-pager">
                  <PageTravelGroup
                    pageNumber={safePage}
                    setPageNumber={setPageNumber}
                    setImageReady={() => {}}
                    isImageProcessing={false}
                    initialTotalPageNumber={totalPageNumber}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Calendar */}
        <div className="qbw-cal" data-tour="ann-calendar">
          <Calendar
            events={calendarEvents}
            onEventClick={(e) => e.route && navigate(e.route)}
          />
        </div>
      </div>
    );
  };

  if (isMinimized) {
    // Dashboard widget: surface the unread announcements first, then fill with
    // recent read ones up to 4.
    const minimizedList = [
      ...announcementDTOList.filter(isUnread),
      ...announcementDTOList.filter((a: any) => !isUnread(a)),
    ].slice(0, 4);
    return (
      <div className="qbw-full">
        <style>{announcementListViewStyles}</style>
        {announcementListLoading ? (
          <BaseLoading
            size="large"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        ) : minimizedList.length === 0 ? (
          <EmptyState compact icon="tray" title={i18n.t("no_announcements")} />
        ) : (
          <div className="qbw-rows">
            {minimizedList.map((a: any) =>
              RenderRow(a, () => navigate("/announcements")),
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="qbw-root">
      <style>{announcementListViewStyles}</style>
      {RenderFullContent()}
    </div>
  );
};

export default AnnouncementListView;
